/**
 * @module Components.StripeForm.TaxBreakdown
 *
 * Displays sales tax breakdown (subtotal, tax, total) with skeleton loading
 */
import React from "react";
import { FormattedNumber } from "react-intl";
import styles from "./TaxBreakdown.module.scss";

export interface TaxBreakdownProps {
  subtotal?: number;
  taxAmount?: number;
  total?: number;
  isLoading?: boolean;
  error?: string | null;
  showPlaceholder?: boolean;
}

/**
 * TaxBreakdown component displays the price breakdown including tax
 */
export default function TaxBreakdown({
  subtotal,
  taxAmount,
  total,
  isLoading = false,
  error = null,
  showPlaceholder = false,
}: TaxBreakdownProps) {
  // Render subtotal (always available from plan amount)
  const renderSubtotal = () => (
    <div className={styles.row}>
      <span className={styles.label}>Subtotal</span>
      <span className={styles.value}>
        {subtotal != null ? (
          <FormattedNumber
            value={subtotal / 100}
            style="currency"
            currency="USD"
          />
        ) : (
          "—"
        )}
      </span>
    </div>
  );

  // Show error state with warning
  if (error) {
    return (
      <div className={styles.taxBreakdown}>
        {renderSubtotal()}
        <div className={styles.row}>
          <span className={styles.label}>Tax</span>
          <span className={styles.placeholder}>⚠️ Enter valid address to calculate</span>
        </div>
        <div className={`${styles.row} ${styles.total}`}>
          <span className={styles.label}>Total</span>
          <span className={styles.value}>—</span>
        </div>
      </div>
    );
  }

  // Show skeleton loading
  if (isLoading) {
    return (
      <div className={styles.taxBreakdown}>
        {renderSubtotal()}
        <div className={styles.row}>
          <span className={styles.label}>Tax</span>
          <span className={styles.value}>
            <div className={styles.skeleton} />
          </span>
        </div>
        <div className={`${styles.row} ${styles.total}`}>
          <span className={styles.label}>Total</span>
          <span className={styles.value}>
            <div className={styles.skeleton} />
          </span>
        </div>
      </div>
    );
  }

  // Show placeholder when address is not complete
  if (showPlaceholder) {
    return (
      <div className={styles.taxBreakdown}>
        {renderSubtotal()}
        <div className={styles.row}>
          <span className={styles.label}>Tax</span>
          <span className={styles.placeholder}>Enter address to calculate</span>
        </div>
        <div className={`${styles.row} ${styles.total}`}>
          <span className={styles.label}>Total</span>
          <span className={styles.value}>—</span>
        </div>
      </div>
    );
  }

  // Show actual data
  return (
    <div className={styles.taxBreakdown}>
      <div className={styles.row}>
        <span className={styles.label}>Subtotal</span>
        <span className={styles.value}>
          <FormattedNumber
            value={(subtotal || 0) / 100}
            style="currency"
            currency="USD"
          />
        </span>
      </div>
      <div className={styles.row}>
        <span className={styles.label}>Tax</span>
        <span className={styles.value}>
          <FormattedNumber
            value={(taxAmount || 0) / 100}
            style="currency"
            currency="USD"
          />
        </span>
      </div>
      <div className={`${styles.row} ${styles.total}`}>
        <span className={styles.label}>Total</span>
        <span className={styles.value}>
          <FormattedNumber
            value={(total || 0) / 100}
            style="currency"
            currency="USD"
          />
        </span>
      </div>
    </div>
  );
}
